import React, { useEffect, useRef, useState } from "react";
import { ArrowCounterClockwise, Copy, Eye, Flask, PencilSimple } from "@phosphor-icons/react";
import { BTN_WHITE } from "@/components/pageSettings/AdminModal";
import { BlockThankYou } from "@/pages/landing/thankYou/ThankYouBlocks";
import { sampleCtx } from "./designPreview";

const PAGE_W = 1100;
const SMALL = `${BTN_WHITE} !px-2 !py-1`;
export const CLASSIC_URL = "/lp/thank-you?preview=1&ty_design=classic&firm=Sample%20Law%20Firm&phone=5555550100";

// The real thank-you page for a design, at desktop width.
export function TemplatePage({ t, shared, height = 1500 }) {
  if (t.blocks?.length) return <BlockThankYou blocks={t.blocks} ctx={{ ...sampleCtx(shared), compact: false }} />;
  return <iframe title="Classic thank-you page" src={CLASSIC_URL} style={{ width: PAGE_W, height, border: 0 }} data-testid="ty-classic-frame" />;
}

// Shrinks a desktop-width page to fit the card.
function Scaled({ children, height }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.4);
  useEffect(() => {
    const el = ref.current;
    if (!el) { return undefined; }
    const ro = new ResizeObserver(() => setScale(el.clientWidth / PAGE_W));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="relative overflow-hidden border-2 border-black bg-white" style={{ height }}>
      <div className="pointer-events-none absolute left-0 top-0 origin-top-left" style={{ width: PAGE_W, transform: `scale(${scale})` }}>{children}</div>
    </div>
  );
}

export function TestPick({ tid, picked, onPick, disabled }) {
  return (
    <label className={`ml-auto flex cursor-pointer items-center gap-1 border-2 border-black px-1.5 py-0.5 text-[10px] uppercase tracking-widest transition-colors duration-100 ${picked ? "bg-black text-white" : "bg-white"} ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
      title={disabled ? "You can test up to 4 designs" : "Pick for a design test"} data-testid={`ty-test-pick-${tid}`}>
      <input type="checkbox" className="accent-emerald-600" checked={picked} disabled={disabled} onChange={onPick} />
      Test
    </label>
  );
}

// Shown on a card whose design is in the running overall test.
export function TestBadge({ tid, row, minViews }) {
  if (!row) return null;
  return (
    <span className="inline-flex items-center gap-1 border border-black bg-sky-200 px-1 text-[9px] uppercase tracking-widest"
          title={`${row.views} of the ${minViews} views each design needs before a winner is picked`} data-testid={`ty-test-badge-${tid}`}>
      <Flask size={10} weight="bold" /> Being tested ·{" "}
      <span className="font-bold" data-testid={`ty-test-badge-views-${tid}`}>{row.views.toLocaleString()}</span>
      {row.views >= minViews ? " views · target reached" : ` of ${minViews} views`}
    </span>
  );
}

export function TemplateCard({ t, shared, isDefault, onPreview, onEdit, onReset, onCopy, onDefault, picked, onPick, pickDisabled, testRow, testMin }) {
  const tid = t.key.replace(":", "-");
  return (
    <div className={`flex flex-col gap-2 border-2 p-3 ${picked ? "border-emerald-600 ring-2 ring-emerald-600" : "border-black"} ${isDefault ? "bg-emerald-50" : "bg-white"}`} data-testid={`ty-gallery-card-${tid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-chivo text-lg font-black leading-tight">{t.name}</span>
        {t.edited && <span className="border border-black bg-yellow-300 px-1 text-[9px] uppercase tracking-widest" data-testid={`ty-gallery-edited-${tid}`}>Edited</span>}
        {isDefault && <span className="border border-black bg-emerald-600 px-1 text-[9px] uppercase tracking-widest text-white" data-testid={`ty-design-default-chip-${tid}`}>Default</span>}
        <TestBadge tid={tid} row={testRow} minViews={testMin} />
        <TestPick tid={tid} picked={picked} onPick={onPick} disabled={pickDisabled} />
      </div>
      <p className="text-[11px] leading-snug text-zinc-600">{t.description}</p>
      <button type="button" onClick={onPreview} className="block w-full text-left transition-opacity duration-150 hover:opacity-90" title="Open full preview" data-testid={`ty-gallery-thumb-${tid}`}>
        <Scaled height={340}><TemplatePage t={t} shared={shared} /></Scaled>
      </button>
      <div className="flex flex-wrap gap-1.5 pt-1">
        <button type="button" className={SMALL} onClick={onPreview} data-testid={`ty-design-preview-${tid}`}><Eye size={11} /> Preview</button>
        <button type="button" className={SMALL} onClick={onEdit} data-testid={`ty-design-edit-${tid}`}><PencilSimple size={11} /> Edit</button>
        {t.edited && <button type="button" className={SMALL} onClick={onReset} data-testid={`ty-design-reset-${tid}`}><ArrowCounterClockwise size={11} /> Reset to original</button>}
        {t.blocks?.length > 0 && <button type="button" className={SMALL} onClick={onCopy} data-testid={`ty-design-copy-${tid}`}><Copy size={11} /> Customize a copy</button>}
        {!isDefault && <button type="button" className={SMALL} onClick={onDefault} data-testid={`ty-design-make-default-${tid}`}>Make default</button>}
      </div>
      {t.template === "classic" && !t.edited && (
        <p className="text-[10px] leading-snug text-zinc-500" data-testid="ty-classic-edit-note">
          Editing Classic turns it into sections you can change, starting from the same parts. &ldquo;Reset to original&rdquo; brings the original back.
        </p>
      )}
    </div>
  );
}
